/**
 * Utility functions for handling disclaimer symbols on product rows
 */

/**
 * Adds disclaimer symbols to line items based on applied discounts
 * Uses precise productCode matching - no fuzzy logic or translation tables
 * @param {Array} lineItems - Array of line item rows, each row should have productCode property
 * @param {Array} summarizedDiscounts - Array of discount objects with DisclaimerSymbol and showDisclaimerOn
 * @returns {Array} Modified lineItems with disclaimer symbols added to Total Price column
 */
export function addDisclaimerSymbolsToLineItems(
   lineItems,
   summarizedDiscounts,
) {
   if (!lineItems || !summarizedDiscounts || summarizedDiscounts.length === 0) {
      return lineItems
   }

   // Create a map of productCode to DisclaimerSymbol for quick lookup
   // Use showDisclaimerOn field to determine which products should have disclaimer symbols
   const disclaimerMap = {}

   summarizedDiscounts.forEach((discount) => {
      // Check both ShowDisclaimerOn (uppercase) and showDisclaimerOn (lowercase) for compatibility
      const showDisclaimerOn =
         discount.ShowDisclaimerOn || discount.showDisclaimerOn

      // Validate that Disclaimer is a non-null, non-undefined, non-empty string
      const hasValidDisclaimer =
         discount.Disclaimer &&
         typeof discount.Disclaimer === 'string' &&
         discount.Disclaimer.trim() !== ''

      // Validate that showDisclaimerOn is a non-empty array or non-empty string
      const hasValidShowOn =
         showDisclaimerOn &&
         (Array.isArray(showDisclaimerOn)
            ? showDisclaimerOn.length > 0
            : typeof showDisclaimerOn === 'string' &&
              showDisclaimerOn.trim() !== '')

      if (discount.DisclaimerSymbol && hasValidDisclaimer && hasValidShowOn) {
         // showDisclaimerOn can be a string, array, or comma-separated string
         let productCodes
         if (Array.isArray(showDisclaimerOn)) {
            productCodes = showDisclaimerOn
         } else if (typeof showDisclaimerOn === 'string') {
            // Handle comma-separated strings like "core1,core2,core3"
            productCodes = showDisclaimerOn
               .split(',')
               .map((code) => code.trim())
         } else {
            productCodes = [showDisclaimerOn]
         }

         productCodes.forEach((productCode) => {
            if (productCode) {
               disclaimerMap[productCode] = disclaimerMap[productCode]
                  ? disclaimerMap[productCode] + discount.DisclaimerSymbol
                  : discount.DisclaimerSymbol
            }
         })
      }
   })

   // If no disclaimers to add, return original lineItems
   if (Object.keys(disclaimerMap).length === 0) {
      return lineItems
   }

   // Process each row to add disclaimer symbols
   return lineItems.map((row) => {
      if (!row || !Array.isArray(row) || row.length === 0) {
         return row
      }

      // Find the productCode cell in the array (has class="productCode")
      const productCodeCell = row.find(
         (cell) => cell && cell.class === 'productCode',
      )
      if (!productCodeCell || !productCodeCell.value) {
         return row // Skip rows without productCode
      }

      // Clone the row to avoid mutating the original
      const newRow = [...row]

      // Check if this row's productCode has a disclaimer symbol
      const DisclaimerSymbol = disclaimerMap[productCodeCell.value]

      if (DisclaimerSymbol) {
         // Find the last cell that contains a currency value (Total Price column)
         for (let i = newRow.length - 1; i >= 0; i--) {
            const cell = newRow[i]
            if (
               cell &&
               cell.value &&
               cell.class &&
               cell.class.includes('currency')
            ) {
               newRow[i] = {
                  ...cell,
                  value: `${cell.value}${DisclaimerSymbol}`,
               }
               break
            }
         }
      }

      return newRow
   })
}
